import React, { useCallback, useMemo, useState } from 'react';
import Layout from '../components/layout/Layout';
import AnalyticsSettings from '../components/analytics/AnalyticsSettings';
import AnalyticsContents from '../components/analytics/AnalyticsContents';
import { exportAnalyticsToPDF } from '../utils/pdfExport';
import { exportAnalyticsToCSV } from '../utils/csvExport';
import '../components/analytics/Analytics.css';

// Keep these labels in sync with AnalyticsSettings.ANALYSIS
const ANALYSIS = {
  TOTAL_BY_CLIENT: 'Monthly Total Insects Trapped',
  CAPTURE_TREND_BY_AREA: 'Monthly Capture Trend Analysis',
  SPECIES_IDENTIFIED_BY_LOCATION: 'Species Identified Analysis of The Month',
  SPECIES_TREND_BY_LOCATION: 'Monthly Species Trend Analysis',
};

// Per-type required fields
const REQUIRED_BY_TYPE = {
  [ANALYSIS.TOTAL_BY_CLIENT]: [
    'type', 'global', 'region', 'client', 'address', 'location', 'device', 'species', 'range',
  ],
  [ANALYSIS.CAPTURE_TREND_BY_AREA]: [
    'type', 'global', 'region', 'client', 'address', 'location', 'device', 'threshold', 'range',
  ],
  [ANALYSIS.SPECIES_IDENTIFIED_BY_LOCATION]: [
    'type', 'global', 'region', 'client', 'address', 'location', 'device', 'species', 'range',
  ],
  [ANALYSIS.SPECIES_TREND_BY_LOCATION]: [
    'type', 'global', 'region', 'client', 'address', 'location', 'device', 'species', 'range',
  ],
};

function expandRangeMissing(range) {
  const missing = [];
  if (!range || !range.from) missing.push('range.from');
  if (!range || !range.to) missing.push('range.to');
  return missing;
}

function validateFilters(filters) {
  const req = REQUIRED_BY_TYPE[filters?.type] || [];
  const missing = [];

  req.forEach((key) => {
    if (key === 'range') {
      missing.push(...expandRangeMissing(filters.range));
      return;
    }
    const v = filters?.[key];
    if (
      v === undefined ||
      v === null ||
      (typeof v === 'string' && v.trim() === '')
    ) {
      missing.push(key);
    }
  });

  const labelFor = (k) => {
    switch (k) {
      case 'type': return 'Type of Analysis';
      case 'global': return 'Global';
      case 'region': return 'Region';
      case 'client': return 'Client';
      case 'address': return 'Address';
      case 'location': return 'Location';
      case 'device': return 'Device';
      case 'species': return 'Species';
      case 'threshold': return 'Threshold';
      case 'range.from': return 'Range (From)';
      case 'range.to': return 'Range (To)';
      default: return k;
    }
  };

  return {
    ok: missing.length === 0,
    message: missing.length
      ? `Please select: ${missing.map(labelFor).join(', ')}.`
      : '',
  };
}

export default function Analytics() {
  // Centralized filters for Settings + Contents
  const [filters, setFilters] = useState({
    type: '',
    global: '',
    region: '',
    client: '',
    address: '',
    location: '',
    device: '',
    species: '',
    threshold: '',
    range: null, // { from: 'YYYY-MM', to: 'YYYY-MM' }
  });

  // Result payload flag/timestamp (actual data will be assembled inside AnalyticsContents)
  const [result, setResult] = useState(null);

  // Store the data payload for CSV export
  const [dataPayload, setDataPayload] = useState(null);

  // Inline error message for validation
  const [errorMsg, setErrorMsg] = useState('');

  // Exporting state
  const [exporting, setExporting] = useState(false);

  // Merge patch from Settings; clear old result and any error on change
  const onChange = useCallback((patch) => {
    setResult(null);
    setDataPayload(null);
    setErrorMsg('');
    setFilters((prev) => ({ ...prev, ...patch }));
  }, []);

  // Button enable: still only requires a Type
  const canGenerate = useMemo(() => Boolean(filters.type), [filters.type]);

  // Download button enabled only after Generate has been clicked successfully
  const canDownload = useMemo(() => Boolean(result && dataPayload), [result, dataPayload]);

  const onGenerate = useCallback(() => {
    const v = validateFilters(filters);
    if (!v.ok) {
      setResult(null);
      setDataPayload(null);
      setErrorMsg(v.message);
      return;
    }
    setErrorMsg('');
    setResult({
      generatedAt: new Date().toISOString(),
    });
  }, [filters]);

  const onDownload = useCallback(async () => {
    if (!result || !filters.type || !dataPayload) {
      return;
    }

    setExporting(true);
    try {
      // Export both PDF and CSV
      const [pdfResult, csvResult] = await Promise.all([
        exportAnalyticsToPDF(filters.type, filters),
        exportAnalyticsToCSV(filters.type, filters, dataPayload)
      ]);

      const errors = [];
      if (!pdfResult.success) {
        errors.push(`PDF export failed: ${pdfResult.error}`);
      }
      if (!csvResult.success) {
        errors.push(`CSV export failed: ${csvResult.error}`);
      }

      if (errors.length > 0) {
        alert(errors.join('\n'));
      }
    } catch (error) {
      console.error('Export error:', error);
      alert(`Error exporting files: ${error.message}`);
    } finally {
      setExporting(false);
    }
  }, [filters, result, dataPayload]);

  // Callback to receive data payload from AnalyticsContents
  const onDataLoaded = useCallback((payload) => {
    setDataPayload(payload);
  }, []);

  return (
    <Layout>
      <div className="anl-page">
        <AnalyticsSettings
          value={filters}
          onChange={onChange}
          onGenerate={onGenerate}
          onDownload={onDownload}
          canGenerate={canGenerate}
          canDownload={canDownload && !exporting}
          exporting={exporting}
          errorMsg={errorMsg}
        />
        <AnalyticsContents 
          filters={filters} 
          result={result} 
          onDataLoaded={onDataLoaded}
        />
      </div>
    </Layout>
  );
}